import { useCallback, useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { useTranslate } from "@similarweb/i18n-provider";
import { Crown } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton, Modal } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { shouldSeeReactivationBanner } from "app/common/utils";
import { useDunnings } from "app/components/hooks/useDunnings";
import { AfterTrialPopupContainer } from "app/components/React/AfterTrialPopup/AfterTrialPopupContainer";
import { getDefaultPaymentPeriod } from "app/components/React/AfterTrialPopup/utils";
import useRequestFeatureHookWithLoading from "app/pages/sales-intelligence/hooks/useRequestFeatureHookWithLoading";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";
import afterTrialEntryModalTrackingService from "app/pages/sales-intelligence/services/tracking/after-trial-entry-modal/trackingService";
import { isSIAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import {
    AFTER_TRIAL_DIALOG_TITLE_ID,
    AFTER_TRIAL_ILLUSTRATION_URL,
    AFTER_TRIAL_PORTAL_CONTAINER_ID,
    BULLET_KEYS,
} from "./constants";
import { useAfterTrialEntryModalDomGuard } from "./hooks/useAfterTrialEntryModalDomGuard";
import { useAfterTrialEntryModalFocusTrap } from "./hooks/useAfterTrialEntryModalFocusTrap";
import {
    ActionsRow,
    AfterTrialBlockingOverlay,
    AfterTrialModalPanel,
    BenefitsBlock,
    BulletRow,
    ContactRow,
    GetAccessHeading,
    IllustrationPanel,
    IntroBlock,
    ModalLayout,
    SI_RESTART_TRIAL_MODAL_STYLES,
    SubtitleText,
    TitleHeading,
} from "./styles";

export type AfterTrialEntryModalProps = {
    isImpersonateMode: boolean;
};

export const AfterTrialEntryModal = ({ isImpersonateMode }: AfterTrialEntryModalProps) => {
    const { translate } = useTranslate();
    const { onDunningsButtonClicked } = useDunnings();
    const { isRequestInProgress, requestFeature } = useRequestFeatureHookWithLoading();
    const [isRestartTrialModalOpen, setIsRestartTrialModalOpen] = useState(false);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    const isAfterTrialUser = !isImpersonateMode && isSIAfterTrial(swSettings);

    const canRestartTrial = shouldSeeReactivationBanner();

    useEffect(() => {
        if (!isAfterTrialUser) {
            setPortalContainer(null);
            return;
        }

        const container = document.createElement("div");
        container.id = AFTER_TRIAL_PORTAL_CONTAINER_ID;
        document.body.appendChild(container);
        setPortalContainer(container);

        return () => {
            container.remove();
            setPortalContainer(null);
        };
    }, [isAfterTrialUser]);

    useEffect(() => {
        if (isAfterTrialUser) {
            afterTrialEntryModalTrackingService.trackEntryModalShow();
        }
    }, [isAfterTrialUser]);

    useAfterTrialEntryModalDomGuard({ isEnabled: isAfterTrialUser });

    useAfterTrialEntryModalFocusTrap({
        containerRef: dialogRef,
        isEnabled: isAfterTrialUser && portalContainer != null && !isRestartTrialModalOpen,
    });

    const handleContactUsClick = useCallback(() => {
        if (isRequestInProgress) {
            return;
        }
        afterTrialEntryModalTrackingService.trackContactUsClick();
        void requestFeature("SI contact");
    }, [isRequestInProgress, requestFeature]);

    const handleRestartTrial = useCallback(() => {
        onDunningsButtonClicked("si.after_trial.entry_modal/restart_trial");
        setIsRestartTrialModalOpen(true);
    }, [onDunningsButtonClicked]);

    const handleUpgradeNow = useCallback(() => {
        afterTrialEntryModalTrackingService.trackUpgradeNowClick();
        const billingPeriod = getDefaultPaymentPeriod();
        afterTrialEntryModalTrackingService.trackSiUpgradePackageClick(billingPeriod);
        window.open(solutionUrlsService.getSiUpgradePurchaseUrl(billingPeriod));
    }, []);

    if (!isAfterTrialUser) {
        return null;
    }

    if (!portalContainer) {
        return null;
    }

    return (
        <>
            {createPortal(
                <AfterTrialBlockingOverlay
                    ref={dialogRef}
                    data-automation="si-after-trial-entry-modal-root"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={AFTER_TRIAL_DIALOG_TITLE_ID}
                >
                    <AfterTrialModalPanel data-automation="si-after-trial-entry-modal-layout">
                        <ModalLayout>
                            <IllustrationPanel data-automation="si-after-trial-entry-modal-illustration">
                                <img
                                    src={AFTER_TRIAL_ILLUSTRATION_URL}
                                    alt=""
                                    width={423}
                                    height={272}
                                />
                            </IllustrationPanel>

                            <Stack
                                flex={1}
                                justifyContent="space-between"
                                minWidth={0}
                            >
                                <Stack gap={0}>
                                    <IntroBlock gap={0}>
                                        <TitleHeading
                                            id={AFTER_TRIAL_DIALOG_TITLE_ID}
                                            fontVariant="h3"
                                            color={colorsPalettes.carbon[500]}
                                        >
                                            {translate("si.after_trial_entry_modal.title")}
                                        </TitleHeading>
                                        <SubtitleText
                                            fontVariant="subsectionTitle"
                                            color={colorsPalettes.carbon[400]}
                                        >
                                            {translate("si.after_trial_entry_modal.subtitle")}
                                        </SubtitleText>
                                    </IntroBlock>

                                    <BenefitsBlock gap="12px">
                                        <GetAccessHeading
                                            fontVariant="subTitle3"
                                            color={colorsPalettes.carbon[500]}
                                        >
                                            {translate("si.after_trial_entry_modal.get_access_to")}
                                        </GetAccessHeading>
                                        {BULLET_KEYS.map((key) => (
                                            <BulletRow
                                                key={key}
                                                data-automation={`si-after-trial-entry-modal-bullet-${key}`}
                                            >
                                                <Crown
                                                    size="xs"
                                                    color={colorsPalettes.indigo[300]}
                                                />
                                                <Text
                                                    fontVariant="body2"
                                                    color={colorsPalettes.carbon[400]}
                                                >
                                                    {translate(key)}
                                                </Text>
                                            </BulletRow>
                                        ))}
                                    </BenefitsBlock>

                                    <ContactRow data-automation="si-after-trial-entry-modal-contact">
                                        <Text
                                            fontVariant="body2"
                                            color={colorsPalettes.carbon[400]}
                                        >
                                            {translate("si.after_trial_entry_modal.contact_prompt")}
                                        </Text>
                                        <Text
                                            as="span"
                                            fontVariant="body2"
                                            color={colorsPalettes.blue[400]}
                                            cursor="pointer"
                                            onClick={handleContactUsClick}
                                            data-automation="si-after-trial-entry-modal-contact-link"
                                        >
                                            {translate("si.common.contact_us").toLowerCase()}
                                            {isRequestInProgress ? "..." : ""}
                                        </Text>
                                    </ContactRow>
                                </Stack>

                                <ActionsRow data-automation="si-after-trial-entry-modal-actions">
                                    {canRestartTrial ? (
                                        <Button
                                            type="flat"
                                            onClick={handleRestartTrial}
                                            data-automation="si-after-trial-entry-modal-restart-trial"
                                        >
                                            {translate("si.after_trial_entry_modal.restart-trial")}
                                        </Button>
                                    ) : null}
                                    <IconButton
                                        type="trial"
                                        iconName="sparkles"
                                        onClick={handleUpgradeNow}
                                        data-automation="si-after-trial-entry-modal-upgrade"
                                    >
                                        {translate("si.common.upgrade_now")}
                                    </IconButton>
                                </ActionsRow>
                            </Stack>
                        </ModalLayout>
                    </AfterTrialModalPanel>
                </AfterTrialBlockingOverlay>,
                portalContainer,
            )}

            <Modal
                isOpen={isRestartTrialModalOpen}
                onRequestClose={() => setIsRestartTrialModalOpen(false)}
                customStyles={SI_RESTART_TRIAL_MODAL_STYLES}
                shouldCloseOnOverlayClick
            >
                <AfterTrialPopupContainer
                    setIsOpen={setIsRestartTrialModalOpen}
                    isSI
                />
            </Modal>
        </>
    );
};
